import type { AdminApiOrganization } from '@/types/serializers';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { LuPlus } from 'react-icons/lu';
import { useState, useMemo, useEffect } from 'react';
import useFetchAdminOrganizations from '../../api/useFetchAdminOrganizations';
import useFetchAdminIam from '../../api/useFetchAdminIam';
import useFetchAdminPlans from '../../api/useFetchAdminPlans';
import { AdminButton } from '../../components/AdminButton';
import AdminPlanTag from '../../components/atoms/AdminPlanTag';
import AdminProgressBar from '../../components/atoms/AdminProgressBar';
import AdminLoadingState from '../../components/molecules/AdminLoadingState';
import AdminErrorState from '../../components/molecules/AdminErrorState';
import AdminOrganizationCreateModal from '../../components/organisms/AdminOrganizationCreateModal';
import AdminMultiSelectDropdown from '../../components/molecules/AdminMultiSelectDropdown';
import {
  formatDateTime,
  getRemainingDaysLabel,
  getProgressValue,
  getRemainingDays,
} from '../../utils/formatters';
import { MdError } from 'react-icons/md';

const CONTRACT_WARNING_THRESHOLD_DAYS = 90;
const tableHeaders = ['企業名', '利用中のプラン', '最終ログイン日時'];

export default function AdminOrganizationsPage() {
  const { data: currentUser } = useFetchAdminIam();
  const { data, isLoading, isError } = useFetchAdminOrganizations();
  const { data: plansData } = useFetchAdminPlans();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState<(string | number)[]>([]);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const planFilterOptions = useMemo(() => {
    const options: Array<{ id: string | number; name: string }> = [];
    if (plansData) {
      plansData.forEach((plan) => {
        if (plan.plan_type) {
          options.push({ id: plan.plan_type, name: plan.name });
        }
      });
    }
    options.push({ id: 'expired', name: '契約切れ' });
    return options;
  }, [plansData]);

  // plansDataが読み込まれたら全選択
  useEffect(() => {
    if (plansData && selectedPlans.length === 0) {
      setSelectedPlans(planFilterOptions.map((opt) => opt.id));
    }
  }, [plansData]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (selectedPlans.length === 0) return [];
    if (selectedPlans.length === planFilterOptions.length) return data;

    return data.filter((org: AdminApiOrganization) => {
      const remainingDays = getRemainingDays(org.contract_end_date);
      const isExpired = remainingDays === 0;

      if (selectedPlans.includes('expired') && isExpired) {
        return true;
      }

      if (!isExpired && org.plan_type && selectedPlans.includes(org.plan_type)) {
        return true;
      }

      return false;
    });
  }, [data, selectedPlans, planFilterOptions.length]);

  if (isLoading) return <AdminLoadingState />;
  if (isError || !data)
    return <AdminErrorState message="クライアント一覧の読み込みに失敗しました。" />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="my-0 text-2xl leading-[1.2] font-bold text-[#333]">クライアント管理</h1>
      </div>
      <div className="flex flex-col gap-2">
        <div className="mb-4 flex items-center gap-4">
          <div className="w-[100px]">
            <AdminMultiSelectDropdown
              label="表示項目"
              options={planFilterOptions}
              value={selectedPlans}
              onChange={setSelectedPlans}
            />
          </div>
          {isSuperAdmin && (
            <div className="ml-auto flex items-center gap-4">
              <AdminButton
                label="企業を追加する"
                icon={<LuPlus className="size-5" />}
                onClick={() => setIsCreateOpen(true)}
              />
            </div>
          )}
        </div>
        <div className="overflow-hidden rounded border border-solid border-[#ccc] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]">
          <div className="grid grid-cols-3 border-0 border-b border-solid border-[#ccc] bg-[#F3F7FE]">
            {tableHeaders.map((header) => (
              <div
                key={header}
                className="flex items-center px-6 py-2 text-xs font-bold text-black"
              >
                {header}
              </div>
            ))}
          </div>
          <div className="flex flex-col">
            {filteredData.map((organization: AdminApiOrganization, index: number) => {
              const progress = getProgressValue(organization);
              const remainingDays = getRemainingDays(organization.contract_end_date);
              const isExpired = remainingDays === 0;
              const isWarning =
                !isExpired &&
                remainingDays !== null &&
                remainingDays <= CONTRACT_WARNING_THRESHOLD_DAYS;
              return (
                <Link
                  key={organization.id}
                  to="/admin"
                  search={{ tab: 'organizations', organizationId: organization.id }}
                  className={clsx(
                    'grid w-full cursor-pointer grid-cols-3 border-0 border-b border-solid border-[#ddd] bg-white text-left no-underline hover:bg-[#f8faff]',
                    index === filteredData.length - 1 && 'border-b-0'
                  )}
                >
                  <div className="flex items-center self-stretch px-6 py-0 text-sm font-bold text-[#333]">
                    {organization.name}
                  </div>
                  <div className="flex flex-col justify-center gap-2 px-6 py-2">
                    <div className="flex w-[221px] flex-col gap-1">
                      <div className="flex flex-col gap-2">
                        <AdminPlanTag
                          plan={isExpired ? '契約切れ' : organization.plan}
                          planType={isExpired ? 'expired' : organization.plan_type}
                          className="self-start"
                        />
                        <AdminProgressBar value={progress} remainingDays={remainingDays} />
                      </div>
                      {isWarning && (
                        <div className="flex items-center justify-end gap-0.5">
                          <MdError className="size-[15px] text-[#ff4e4e]" />
                          <div className="flex items-center justify-end">
                            <span className="text-[10px] leading-normal text-[#ff4e4e]">
                              {getRemainingDaysLabel(organization.contract_end_date)}
                            </span>
                          </div>
                        </div>
                      )}
                      {!isWarning && (
                        <div className="flex items-center justify-end">
                          <span className="text-[10px] leading-normal text-[#40567a]">
                            {getRemainingDaysLabel(organization.contract_end_date)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 self-stretch px-6 py-0 text-sm text-[#333]">
                    <span>{formatDateTime(organization.staff_last_sign_in_at)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        <div className="text-right text-xs leading-[1.2] text-[#333]">
          {filteredData.length} アカウント作成済
        </div>
      </div>
      {isSuperAdmin && (
        <AdminOrganizationCreateModal open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      )}
    </div>
  );
}
